/**
 * The three filters Søknader, Kalender and Saldo share: a search, a team and a category. One
 * state for the page, so switching tab keeps what the manager narrowed to.
 *
 * Teams are the person's teams in the active company only: GET /users sends every membership's
 * teams, and a person in two companies would otherwise offer the other company's team names
 * (the same rule as Arbeidstid.tsx teamsOf).
 */
import type { ApiAbsence, ApiUser } from '@/api/data';
import { fullName } from '@/ui/format';
import { type Category, catOf } from './category';
import { spanOf } from './dates';

export interface Filter {
  q: string;
  /** a team name, or '' for every team */
  team: string;
  /** a category, or '' for every kind */
  cat: Category | '';
}
export const NO_FILTER: Filter = { q: '', team: '', cat: '' };

export const teamsOf = (u: Pick<ApiUser, 'teams'> | null | undefined, tenantId: number | undefined) =>
  (u?.teams ?? []).filter((t) => {
    const tid = (t as { tenant_id?: number }).tenant_id;
    return tid == null || tid === tenantId;
  });

export const teamNames = (users: Pick<ApiUser, 'teams'>[], tenantId: number | undefined) =>
  [...new Set(users.flatMap((u) => teamsOf(u, tenantId).map((t) => t.name)))].sort((a, b) =>
    a.localeCompare(b, 'nb'),
  );

const has = (text: string | null | undefined, needle: string) => (text ?? '').toLowerCase().includes(needle);

/**
 * The holiday years the Fravær page offers: last, this and next, around the current one. The current one is
 * the calendar year of today, as everywhere else in the app (ferieår = kalenderår, ferieloven § 5 (1);
 * backend/services/absenceStanding.js, Saldo, Ferieliste and the queue's balance column).
 */
export const holidayYears = (today: string) => {
  const y = Number(today.slice(0, 4));
  return [y - 1, y, y + 1];
};

/** An absence belongs to every holiday year it has a day in: 30 December to 2 January is in both. */
export const inHolidayYear = (a: Pick<ApiAbsence, 'start_date' | 'end_date'>, year: number) => {
  const { from, to } = spanOf(a);
  return from <= `${year}-12-31` && to >= `${year}-01-01`;
};

/** A person passes the team and the search (their name or a team name). */
export function personMatches(
  u: Pick<ApiUser, 'name' | 'lastname' | 'teams'>,
  f: Filter,
  tenantId: number | undefined,
): boolean {
  const teams = teamsOf(u, tenantId).map((t) => t.name);
  if (f.team && !teams.includes(f.team)) return false;
  const n = f.q.trim().toLowerCase();
  return !n || has(fullName(u), n) || teams.some((t) => has(t, n));
}

/**
 * An absence passes the category, its person's team, and a search on the person, the type's
 * name or the note. `u` is the full person from GET /users, which carries the teams the
 * absence's own `user` does not.
 */
export function absenceMatches(
  a: Pick<ApiAbsence, 'absence_type' | 'reason' | 'user'>,
  u: Pick<ApiUser, 'name' | 'lastname' | 'teams'> | undefined,
  f: Filter,
  tenantId: number | undefined,
): boolean {
  if (f.cat && catOf(a.absence_type) !== f.cat) return false;
  const person = u ?? (a.user ? { ...a.user, teams: [] } : { name: '', lastname: '', teams: [] });
  if (!personMatches(person, { ...f, q: '' }, tenantId)) return false;
  const n = f.q.trim().toLowerCase();
  return (
    !n ||
    personMatches(person, { ...f, team: '' }, tenantId) ||
    has(a.absence_type?.name, n) ||
    has(a.reason, n)
  );
}
